import { ExternalLink, Users, Sparkles, Loader2, CheckCircle2 } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import type { LinkedInProfile } from './use-search'

// --- Avatar initials ---

function getInitials(name: string): string {
  const parts = name.trim().split(' ')
  if (parts.length >= 2) {
    return `${parts[0]?.[0] ?? ''}${parts[parts.length - 1]?.[0] ?? ''}`.toUpperCase()
  }
  return name.slice(0, 2).toUpperCase()
}

// --- Main card ---

type SearchResultCardProps = {
  readonly profile: LinkedInProfile
  readonly isSelected: boolean
  readonly isEnriching: boolean
  readonly onSelect: (id: string, selected: boolean) => void
  readonly onImport: (profile: LinkedInProfile) => void
  readonly onEnrich: (profile: LinkedInProfile) => void
}

export function SearchResultCard({
  profile,
  isSelected,
  isEnriching,
  onSelect,
  onImport,
  onEnrich,
}: SearchResultCardProps) {
  const isPreview = !profile.is_enriched

  return (
    <Card
      className={cn(
        'transition-colors',
        isSelected && 'ring-2 ring-primary',
        // Matches the table-row tint so imported leads read identically in
        // both views: faint emerald wash + emerald border accent.
        profile.in_pipeline && 'border-emerald-500/30 bg-emerald-500/[0.04]',
        isPreview && !profile.in_pipeline && 'border-dashed',
      )}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {/* Checkbox — for previews this selects the lead for batch-enrich;
              for enriched leads it selects for batch-import. */}
          {!profile.in_pipeline && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => onSelect(profile.id, e.target.checked)}
              className="mt-1 h-4 w-4 shrink-0 cursor-pointer rounded border-[rgba(255,255,255,0.08)] text-primary focus:ring-primary"
              aria-label={`Select ${profile.name}`}
            />
          )}
          {profile.in_pipeline && <div className="mt-1 h-4 w-4 shrink-0" />}

          {/* Avatar */}
          <Avatar className="h-10 w-10 shrink-0">
            {profile.avatar_url && (
              <AvatarImage src={profile.avatar_url} alt={profile.name} />
            )}
            <AvatarFallback className="text-xs font-semibold">
              {getInitials(profile.name)}
            </AvatarFallback>
          </Avatar>

          {/* Info */}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-sm truncate">{profile.name}</span>
              {isPreview && !profile.in_pipeline && (
                <Badge variant="outline" className="shrink-0 text-xs">
                  Preview
                </Badge>
              )}
            </div>

            <p className="mt-0.5 text-sm text-muted-foreground truncate">
              {[profile.title, profile.company].filter(Boolean).join(' at ')}
            </p>

            <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              {profile.location && <span>{profile.location}</span>}
              {profile.mutual_connections > 0 && (
                <span className="flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {profile.mutual_connections} mutual
                </span>
              )}
              {profile.linkedin_url && (
                <a
                  href={profile.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-[var(--color-cobalt-bright)] hover:text-[var(--color-cobalt-bright)] hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink className="h-3 w-3" />
                  View Profile
                </a>
              )}
              {isPreview && (
                <span className="italic">
                  Enrich to unlock LinkedIn URL, full name, location, photo
                </span>
              )}
            </div>
          </div>

          {/* Action — "Added" badge for imported leads, Enrich for previews,
              Import for already-enriched leads. The badge sits in the same
              slot the Import button occupied so the row keeps a stable layout
              when it flips. */}
          <div className="shrink-0">
            {profile.in_pipeline ? (
              <Badge className="gap-1 text-xs bg-emerald-500/15 text-emerald-300 border-emerald-500/30">
                <CheckCircle2 className="h-3 w-3" />
                Added
              </Badge>
            ) : isPreview ? (
              <Button
                size="sm"
                variant="default"
                onClick={() => onEnrich(profile)}
                disabled={isEnriching || !profile.apollo_id}
                className="gap-1 text-xs"
              >
                {isEnriching ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Enriching…
                  </>
                ) : (
                  <>
                    <Sparkles className="h-3 w-3" />
                    Enrich (1 credit)
                  </>
                )}
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onImport(profile)}
                className="text-xs"
              >
                Import
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
